import { Auth, skipCSRFCheck } from "@auth/core";
import { NEXTAUTH_SECRET } from "@pharaday/env";
import { injectable } from "inversify";
import NextAuth, {
  CredentialsSignin as CredentialsSigninError,
  type NextAuthConfig,
  type NextAuthRequest,
  type NextAuthResult,
} from "next-auth";
import CredentialsProvider from "next-auth/providers/credentials";
import type { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";
import type { NextMiddlewareResult } from "next/dist/server/web/types";
import { type NextFetchEvent, type NextRequest, NextResponse } from "next/server";
import { pathToRegexp } from "path-to-regexp";

import { isUrl, useSecureCookies } from "../../../utils";
import {
  AuthenticatorProvider,
  type AuthMiddlewareConfiguration,
  type AuthProvider,
  type Credentials,
  type SsoRouteHandlerConfiguration,
} from "../../application";
import { AuthContainer } from "../../auth.container";
import { AccessToken, AuthErrorEnum, type Session } from "../../domain";

export const COOKIE_SIGN_IN_CALLBACK_URL = "signInCallbackUrl";

const API_AUTH_PREFIX = "/api/auth";

/** Credentials reach `authorize` as form fields, so a checked box comes back as the string "true". */
const isAccepted = (value: unknown): boolean => value === true || value === "true";

class AuthEmailVerificationRequiredError extends CredentialsSigninError {
  constructor(pendingToken: string) {
    super();
    this.code = JSON.stringify({
      reason: "AUTH_EMAIL_VERIFICATION_REQUIRED",
      pendingToken,
    });
  }
}

@injectable()
export class NextAuthProvider implements AuthProvider {
  private nextAuth: NextAuthResult;

  constructor() {
    this.nextAuth = NextAuth(this.getNextAuthConfig());
  }

  private getNextAuthConfig(): NextAuthConfig {
    return {
      secret: AuthContainer.get<string>(NEXTAUTH_SECRET),
      providers: [
        CredentialsProvider({
          authorize: async params => {
            if (!params) {
              return null;
            }

            const credentials = params as Credentials;
            try {
              switch (credentials.type) {
                case "with-password": {
                  const result = await AuthContainer.get(AuthenticatorProvider).signInWithPassword(
                    credentials.email,
                    credentials.password
                  );

                  if ("pendingToken" in result) {
                    throw new AuthEmailVerificationRequiredError(result.pendingToken);
                  }

                  return result;
                }

                case "with-magic-link": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithMagicLink(
                    credentials.email,
                    credentials.code
                  );
                }

                case "with-totp": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithTotp(
                    credentials.pendingToken,
                    credentials.challenge,
                    credentials.code
                  );
                }

                case "with-email-validation": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithValidationEmail(
                    credentials.pendingToken,
                    credentials.code
                  );
                }

                case "with-sso": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithSso(credentials.code);
                }

                case "with-self-signup": {
                  const { accessToken, refreshToken, containerId, invitationId } = await AuthContainer.get(
                    AuthenticatorProvider
                  ).completeSelfSignUp(credentials.token, credentials.code, credentials.name, {
                    acceptsTermsAndConditions: isAccepted(params.acceptsTermsAndConditions),
                    acceptsPrivacyPolicy: isAccepted(params.acceptsPrivacyPolicy),
                  });

                  return { accessToken, refreshToken, selfSignUp: { containerId, invitationId } };
                }
              }
            } catch (error) {
              if (error instanceof AuthEmailVerificationRequiredError) {
                throw error;
              }

              return null;
            }
          },
        }),
      ],
      session: { strategy: "jwt" },
      callbacks: {
        jwt: async ({ token, user }) => {
          if (user) {
            return { ...token, ...user };
          }

          if (Date.now() < token.accessToken.expiresAt * 1000) {
            return token;
          }

          try {
            const credential = await AuthContainer.get(AuthenticatorProvider).requestRefreshToken(
              token.accessToken,
              token.refreshToken
            );

            return {
              accessToken: credential.accessToken,
              refreshToken: credential.refreshToken,
            };
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
          } catch (_error) {
            return { ...token, error: AuthErrorEnum.REFRESH_ACCESS_TOKEN_ERROR as const };
          }
        },

        session(params) {
          const { session } = params;

          if ("token" in params) {
            session.accessToken = params.token.accessToken;
            session.error = params.token.error;
            session.selfSignUp = params.token.selfSignUp;
            return session;
          }

          // TODO: If use UserAdapter
          // if ("user" in params) {
          //    ...
          // }

          return session;
        },
      },
      events: {
        signOut: async params => {
          if ("token" in params && params.token) {
            await AuthContainer.get(AuthenticatorProvider).signOut(params.token.accessToken, params.token.refreshToken);
          }
        },
      },
    };
  }

  getRouteHandlers() {
    const { GET, POST } = this.nextAuth.handlers;
    return { GET, POST };
  }

  getSsoRouteHandlers(configuration: SsoRouteHandlerConfiguration) {
    const GET = async (req: NextRequest): Promise<NextResponse> => {
      const searchParams = req.nextUrl.searchParams;
      const code = searchParams.get("code");

      const ssoErrorRedirect = NextResponse.redirect(
        new URL(
          `${configuration.signInUrl}?error=${AuthErrorEnum.SSO_ERROR}`,
          !isUrl(configuration.signInUrl) ? req.nextUrl : undefined
        )
      );

      if (!code) {
        return ssoErrorRedirect;
      }

      try {
        const { response } = await this.signIn({ type: "with-sso", code }, req);

        const signInCallbackUrl =
          req.cookies.get(COOKIE_SIGN_IN_CALLBACK_URL)?.value ??
          this.buildSignInCallbackUrl(req, configuration.signInCallbackUrl);

        const location = response?.headers.get("location");
        const error = location && new URL(location).searchParams.get("error");
        if (error) {
          return ssoErrorRedirect;
        }

        return NextResponse.redirect(new URL(signInCallbackUrl), response);
        // eslint-disable-next-line sonarjs/no-ignored-exceptions
      } catch (_error) {
        console.log("🚀 ~ NextAuthProvider ~ GET ~ _error:", _error);
        return ssoErrorRedirect;
      }
    };

    return { GET };
  }

  toSession(nextAuthSession: Session) {
    return {
      accessToken: new AccessToken(nextAuthSession.accessToken.value),
    };
  }

  authMiddleware(configuration: AuthMiddlewareConfiguration) {
    const applyAfterAuthMiddleware = async (req: NextAuthRequest, event: NextFetchEvent) => {
      return (await configuration.afterAuth?.(req, event)) ?? NextResponse.next();
    };

    const handleGuestRoute = async (req: NextAuthRequest, event: NextFetchEvent) => {
      const isSignedIn = req.auth && !req.auth.error;

      if (isSignedIn) {
        const signInCallbackUrl = this.buildSignInCallbackUrl(req, configuration.signInCallbackUrl);
        return NextResponse.redirect(signInCallbackUrl);
      }

      return applyAfterAuthMiddleware(req, event);
    };

    const nextAuthMiddleware = this.nextAuth.auth(async (req: NextAuthRequest, event: NextFetchEvent) => {
      const { nextUrl } = req;
      const pathname = nextUrl.pathname;

      const routeMatchPathname = (route: string) => pathToRegexp(route).regexp.test(pathname);

      const isSignedIn = req.auth && !req.auth.error;
      const isApiAuthRoute = pathname.startsWith(API_AUTH_PREFIX);
      const isGuestRoute = configuration.guestRoutes?.some(routeMatchPathname);

      const isPublicRoute = configuration.publicRoutes?.some(routeMatchPathname);

      if (isApiAuthRoute) {
        return undefined;
      }

      if (isGuestRoute) {
        return await handleGuestRoute(req, event);
      }

      if (!isSignedIn && !isPublicRoute) {
        const error = req.auth?.error;
        const signInUrl = new URL(
          error ? `${configuration.signInUrl}?error=${error}` : configuration.signInUrl,
          !isUrl(configuration.signInUrl) ? nextUrl : undefined
        );
        const response = NextResponse.redirect(signInUrl);
        response.cookies.set(COOKIE_SIGN_IN_CALLBACK_URL, nextUrl.toString(), {
          httpOnly: true,
          secure: useSecureCookies(req),
          sameSite: "lax",
          path: "/",
        });
        response.headers.set("x-current-path", pathname);
        return response;
      }

      const response = await applyAfterAuthMiddleware(req, event);

      if (isSignedIn && req.cookies?.get(COOKIE_SIGN_IN_CALLBACK_URL)) {
        response.cookies.delete(COOKIE_SIGN_IN_CALLBACK_URL);
      }

      response.headers.set("x-current-path", pathname);

      return response;
    });

    return async (request: NextRequest, event: NextFetchEvent) => {
      const response = await nextAuthMiddleware(request, event);
      return this.sessionMiddleware(request, response);
    };
  }

  // Remove all session cookies if not next-auth api route
  private sessionMiddleware(request: NextRequest, response: NextMiddlewareResult) {
    if (response && !request.nextUrl.pathname.startsWith(API_AUTH_PREFIX)) {
      const nonSessionCookies = response.headers.getSetCookie().filter(cookie => {
        return !cookie.startsWith("authjs.session-token");
      });
      response.headers.delete("Set-Cookie");
      for (const cookie of nonSessionCookies) {
        response.headers.append("Set-Cookie", cookie);
      }
    }

    return response;
  }

  getSignInCallbackUrl(cookie: ReadonlyRequestCookies) {
    return cookie.get(COOKIE_SIGN_IN_CALLBACK_URL)?.value;
  }

  /** Credentials are posted as form fields, so every value has to be sent as a string. */
  protected toFormFields(credentials: Credentials): [string, string][] {
    return Object.entries(credentials).map(([field, value]) => [field, String(value)]);
  }

  private buildSignInCallbackUrl(req: NextRequest, signInCallbackUrl?: string) {
    return signInCallbackUrl
      ? // eslint-disable-next-line sonarjs/no-nested-conditional
        new URL(signInCallbackUrl, !isUrl(signInCallbackUrl) ? req.nextUrl : undefined)
      : new URL("/", req.nextUrl);
  }

  async signIn(
    credentials: Credentials,
    request?: NextRequest
  ): Promise<{ success: boolean; response?: NextResponse }> {
    if (!request) {
      // Defensive programming, this should not ever be happened
      throw new Error("Method not implemented.");
    }

    const url = new URL(request.nextUrl.origin + "/api/auth/callback/credentials");
    const authRequest = new Request(url, { method: "POST", body: new URLSearchParams(this.toFormFields(credentials)) });

    const response = await Auth(authRequest, {
      ...this.getNextAuthConfig(),
      basePath: "/api/auth",
      trustHost: true,
      skipCSRFCheck: skipCSRFCheck,
    });

    return { success: true, response: new NextResponse(response.body, response) };
  }

  async signOut(): Promise<{ success: boolean }> {
    await this.nextAuth.signOut();
    return { success: true };
  }

  async getSession(): Promise<Session | null> {
    return this.nextAuth.auth();
  }

  async updateSession(session: Session): Promise<void> {
    await this.nextAuth.unstable_update(session);
  }

  async renewSession(): Promise<Session | null> {
    const nextAuthSession = await this.nextAuth.auth();
    if (!nextAuthSession) {
      return null;
    }

    const session = this.toSession(nextAuthSession);
    return !session.accessToken.isExpired() ? session : null;
  }
}
